import logo from '@/assets/hiredcoach-logo.svg';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, Briefcase, CheckCircle, FileText, MessageSquare } from 'lucide-react';
import { Link } from 'react-router-dom';

const NextSteps = () => {
  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="flex justify-center mb-6">
            <img src={logo} alt="HiredCoach Logo" className="w-36 h-36" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900">
            How <span className="text-[#4f9ecb]">Hired</span><span className="text-[#334763]">Coach</span> Works
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Get interview-ready in three simple steps. We'll help you practice and perfect your responses.
          </p>
        </div>

        {/* Steps Flow */}
        <div className="relative">
          {/* Connection Lines */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-green-200 via-blue-200 to-orange-200 transform -translate-y-1/2 z-0"></div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 relative z-10 lg:items-stretch">
            {/* Step 1: Resume */}
            <div className="flex flex-col items-center text-center space-y-6 h-full">
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center shadow-lg">
                  <CheckCircle className="w-10 h-10 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 font-bold text-sm">✓</span>
                </div>
              </div>
              
              <Card className="w-full max-w-sm bg-green-50 border-green-200 hover:shadow-md transition-shadow flex-1">
                <CardContent className="p-6 h-full flex flex-col justify-between">
                  <div className="space-y-4">
                    <div className="flex items-center justify-center space-x-2">
                      <h3 className="text-xl font-semibold text-green-800">Step 1: Introduce Yourself</h3>
                    </div>
                    <p className="text-green-700 text-sm leading-relaxed">
                      You've already uploaded your resume! We use this to understand your background and tailor interview questions specifically for you.
                    </p>
                  </div>
                  <div className="pt-2">
                    <Button asChild variant="outline" className="w-full bg-white border-green-300 text-green-700 hover:bg-green-50">
                      <Link to="/me">
                        Edit your info
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Step 2: Job Listing */}
            <div className="flex flex-col items-center text-center space-y-6 h-full">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                <Briefcase className="w-10 h-10 text-white" />
              </div>
              
              <Card className="w-full max-w-sm bg-blue-50 border-blue-200 hover:shadow-md transition-shadow flex-1">
                <CardContent className="p-6 h-full flex flex-col justify-between">
                  <div className="space-y-4">
                    <div className="flex items-center justify-center space-x-2">
                      <h3 className="text-xl font-semibold text-blue-800">Step 2: Add a Job</h3>
                    </div>
                    <p className="text-blue-700 text-sm leading-relaxed">
                      Find a listing for your dream job and paste it into <span className="font-bold text-[#4f9ecb]">Hired</span><span className="font-bold text-[#334763]">Coach</span>. We'll analyze the requirements, study the industry, and create a tailored interview.
                    </p>
                  </div>
                  <div className="pt-2">
                    <Button asChild className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                      <Link to="/jobs/new">
                        Got a job in mind?
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Step 3: Interview */}
            <div className="flex flex-col items-center text-center space-y-6 h-full">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center shadow-lg">
                <MessageSquare className="w-10 h-10 text-white" />
              </div>
              
              <Card className="w-full max-w-sm bg-orange-50 border-orange-200 hover:shadow-md transition-shadow flex-1">
                <CardContent className="p-6 h-full flex flex-col justify-between">
                  <div className="space-y-4">
                    <div className="flex items-center justify-center space-x-2">
                      <h3 className="text-xl font-semibold text-orange-800">Step 3: Study Time</h3>
                    </div>
                    <p className="text-orange-700 text-sm leading-relaxed">
                      Take your practice interview and prepare yourself to ace the real thing later. Answer questions with voice or typing, and get detailed actionable feedback.
                    </p>
                  </div>
                  <div className="pt-2">
                    <Button asChild variant="outline" className="w-full bg-white border-orange-300 text-orange-700 hover:bg-orange-50">
                      <Link to="/jobs">
                        View Your Jobs
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-8 space-y-6">
          <h2 className="text-2xl font-bold text-center text-gray-900">What Makes <span className="font-bold text-[#4f9ecb]">Hired</span><span className="text-[#334763] font-bold">Coach</span> Special?</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center space-y-3">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Personalized Questions</h3>
              <p className="text-sm text-gray-600">Questions tailored to your resume and the specific job and industry you want</p>
            </div>
            
            <div className="text-center space-y-3">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto">
                <MessageSquare className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Voice & Text Practice</h3>
              <p className="text-sm text-gray-600">Practice speaking your answers or type them out. Do it in one go or take your time during your lunch break - whatever works for your life</p>
            </div>
            
            <div className="text-center space-y-3">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto">
                <CheckCircle className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Instant Feedback</h3>
              <p className="text-sm text-gray-600">We'll break down your answers, your best moments, and your help you fix up what didn't work. We'll give you insights towards what the hiring manager is thinking</p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center space-y-6 bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
          <h2 className="text-2xl font-bold text-gray-900">Ready to Get Started?</h2>
          <p className="text-gray-600 max-w-xl mx-auto">
            You've already completed step 1! Now tell us about that job you want and start practicing for your dream role.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8">
              <Link to="/jobs/new">
                Add a job
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default NextSteps;
